import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateSchedule, MAINTENANCE_TASK_TYPES, MAINTENANCE_INTERVAL_TYPES } from '../../hooks/useMaintenance'
import { usePrinters } from '../../hooks/usePrinters'
import { BackLink } from '../../components/BackLink'

export default function ScheduleForm() {
  const navigate = useNavigate()
  const { data: printers } = usePrinters()
  const createSchedule = useCreateSchedule()

  const [printerId, setPrinterId] = useState(printers?.[0]?.id ?? '')
  const [taskType, setTaskType] = useState<string>('nozzle-swap')
  const [intervalType, setIntervalType] = useState<'hours' | 'days' | 'prints'>('hours')
  const [intervalValue, setIntervalValue] = useState('200')
  const [errorMessage, setErrorMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')
    try {
      await createSchedule.mutateAsync({
        printer_id: printerId,
        task_type: taskType,
        interval_type: intervalType,
        interval_value: Number(intervalValue),
      })
      navigate('/maintenance/schedule')
    } catch (err) {
      setErrorMessage((err as Error).message)
    }
  }

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 560 }}>
      <BackLink fallback="/maintenance/schedule" />
      <h1>Add maintenance schedule</h1>
      <form onSubmit={handleSubmit} style={{ marginTop: 'var(--space-6)' }}>
        <Field label="Printer" value={printerId} onChange={setPrinterId} selectOptions={(printers ?? []).map((p) => ({ value: p.id, label: p.name }))} required />
        <Field label="Task" value={taskType} onChange={setTaskType} select={MAINTENANCE_TASK_TYPES as unknown as string[]} />
        <Field label="Interval type" value={intervalType} onChange={(v) => setIntervalType(v as 'hours' | 'days' | 'prints')} select={MAINTENANCE_INTERVAL_TYPES as unknown as string[]} />
        <Field label="Interval value" value={intervalValue} onChange={setIntervalValue} type="decimal" required hint={`e.g. every ${intervalValue} ${intervalType}`} />

        <button type="submit" disabled={createSchedule.isPending} style={{ marginTop: 'var(--space-6)', padding: 'var(--space-3) var(--space-5)', background: 'var(--color-primary)', color: 'white', borderRadius: 'var(--radius-md)', fontWeight: 600 }}>
          {createSchedule.isPending ? 'Saving…' : 'Add schedule'}
        </button>
        {errorMessage && (
          <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', marginTop: 'var(--space-3)' }}>{errorMessage}</p>
        )}
      </form>
    </div>
  )
}

function Field({ label, value, onChange, type = 'text', hint, required, select, selectOptions }: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: 'text' | 'date' | 'decimal'
  hint?: string
  required?: boolean
  select?: string[]
  selectOptions?: { value: string; label: string }[]
}) {
  const commonStyle: React.CSSProperties = {
    width: '100%',
    padding: 'var(--space-3)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    background: 'var(--color-bg)',
    color: 'var(--color-text)',
  }
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
        {label} {required && <span style={{ color: 'var(--color-danger)' }}>*</span>}
      </label>
      {selectOptions ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} style={commonStyle}>
          {selectOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      ) : select ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} style={commonStyle}>
          {select.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      ) : (
        <input type={type === 'date' ? 'date' : 'text'} inputMode={type === 'decimal' ? 'decimal' : undefined} required={required} value={value} onChange={(e) => onChange(e.target.value)} style={commonStyle} />
      )}
      {hint && <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginTop: 'var(--space-1)' }}>{hint}</p>}
    </div>
  )
}
